/* GCO: selección de producto por color; no consulta hasta la intención explícita. */
(() => {
  'use strict';
  const pending = new WeakMap();
  const cache = new Map();
  const lifetime = 5 * 60 * 1000;
  let sequence = 0;

  function endpoint(product) {
    const url = new URL(product.url, location.origin);
    if (url.origin !== location.origin || !/\/products\/[^/]+$/.test(url.pathname)) throw new Error('Invalid color product URL');
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', 'gco-color-card');
    return url;
  }

  async function load(product) {
    const url = endpoint(product);
    const key = `${url.href}|${document.documentElement.lang}|${window.Shopify?.country || ''}`;
    const hit = cache.get(key);
    if (hit && hit.expires > Date.now()) return hit.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const promise = fetch(url.href, { signal: controller.signal, credentials: 'same-origin' })
      .then(async response => {
        if (!response.ok) throw new Error('Color card request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = html.querySelector('template[data-color-card]');
        if (!template || template.dataset.productId !== String(product.productId) || (template.dataset.family || '').trim() !== product.family.trim() || !template.content.querySelector('product-card')) {
          throw new Error('Invalid color card response');
        }
        return template;
      })
      .catch(error => { cache.delete(key); throw error; })
      .finally(() => clearTimeout(timeout));
    cache.set(key, { promise, expires: Date.now() + lifetime });
    if (cache.size > 30) cache.delete(cache.keys().next().value);
    return promise;
  }

  function replaceMatches(card, source, selector) {
    const replacement = source.querySelector(selector);
    for (const old of card.querySelectorAll(selector)) {
      if (replacement) old.replaceWith(replacement.cloneNode(true));
      else old.remove();
    }
  }

  function prepare(card, template, product) {
    const next = card.cloneNode(true);
    const source = template.content;
    const donor = source.querySelector('product-card');
    const simple = next.classList.contains('product-card-simple');
    for (const name of ['productId', 'variantId', 'productPrice', 'compareAtPrice']) next.dataset[name] = donor.dataset[name] || '';
    next.dataset.productVariantsSize = template.dataset.variantsSize;
    next.removeAttribute('data-no-swatch-selected');
    next.removeAttribute('data-pf-price-applied');
    for (const flag of next.querySelectorAll('.product-flags__item')) flag.remove();
    for (const gallery of next.querySelectorAll('[data-media-hydrated]')) gallery.removeAttribute('data-media-hydrated');
    next.removeAttribute('aria-busy');
    next.removeAttribute('view-event-payload');
    const image = donor.querySelector('.product-card-simple__image');
    if (image) next.dataset.featuredMediaUrl = image.src;
    for (const node of next.querySelectorAll('[data-product-id]')) node.dataset.productId = String(product.productId);
    for (const node of next.querySelectorAll('[data-product-url]')) node.dataset.productUrl = product.url;
    for (const node of next.querySelectorAll('[data-product-title]')) node.dataset.productTitle = product.title;
    for (const link of next.querySelectorAll('[ref="productCardLink"], [ref="productTitleLink"], [ref="cardGalleryLink"]')) {
      const url = new URL(link.getAttribute('href') || product.url, location.origin);
      url.pathname = new URL(product.url, location.origin).pathname;
      url.searchParams.delete('variant');
      link.setAttribute('href', url.pathname + url.search);
      if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', product.title);
    }
    const title = next.querySelector('[ref="productTitleLink"] h3, [ref="productTitleLink"] .text-block');
    if (title) title.textContent = product.title;
    const hiddenTitle = next.querySelector('[ref="productCardLink"] > .visually-hidden');
    if (hiddenTitle) hiddenTitle.textContent = product.title;
    for (const heading of next.querySelectorAll('.product-grid-view-zoom-out--details h3')) heading.textContent = product.title;

    if (simple) {
      const oldImage = next.querySelector('.product-card-simple__image');
      if (oldImage && image) {
        const replacement = image.cloneNode(true);
        replacement.sizes = oldImage.sizes;
        oldImage.replaceWith(replacement);
      }
      replaceMatches(next, donor, '.product-card-simple__native-price');
      replaceMatches(next, donor, '.product-card-simple__badges');
    } else {
      const slideshow = next.querySelector('slideshow-component');
      const scroller = slideshow?.querySelector('slideshow-slides');
      if (scroller) {
        const oldImage = scroller.querySelector('img');
        const media = [...source.querySelector('[data-color-card-gallery]').children].map(slide => slide.cloneNode(true));
        for (const slide of media) {
          const img = slide.querySelector('img');
          if (img && oldImage) img.sizes = oldImage.sizes;
        }
        const deferred = document.createElement('template');
        deferred.setAttribute('data-deferred-media', '');
        deferred.content.append(...media.slice(1));
        scroller.replaceChildren(...media.slice(0, 1), ...(media.length > 1 ? [deferred] : []));
        const arrows = source.querySelector('[data-color-card-controls] slideshow-arrows');
        const oldArrows = slideshow.querySelector('slideshow-arrows');
        if (oldArrows) {
          if (arrows) oldArrows.replaceWith(arrows.cloneNode(true)); else oldArrows.remove();
        } else if (arrows) scroller.before(arrows.cloneNode(true));
        slideshow.setAttribute('initial-slide', '0');
        slideshow.setAttribute('data-generic-media-size', String(media.length));
        slideshow.style.setProperty('--slideshow-timeline', media.map((_, i) => `--slide-${i}`).join(', '));
      }
      replaceMatches(next, source, '.product-badges');
      const price = source.querySelector('[data-color-card-price] .flags-native-price');
      if (price) for (const old of next.querySelectorAll('.flags-native-price')) old.replaceWith(price.cloneNode(true));
    }
    for (const selector of ['wishlist-button', 'quick-add-custom-component', 'quick-add-component', '.custom-price']) replaceMatches(next, source, selector);
    // Los slots diferidos cambian de identidad sin iniciar su descarga.
    for (const slot of next.querySelectorAll('[data-quick-add-lazy]')) { slot.hidden = true; delete slot.dataset.loading; }
    for (const node of next.querySelectorAll('[data-pf-base-cents]')) {
      node.dataset.pfBaseCents = donor.dataset.productPrice;
      node.dataset.pfBaseVariantId = donor.dataset.variantId;
    }
    for (const node of next.querySelectorAll('[data-auto-discount-price], [data-auto-discount-badge]')) { node.hidden = true; node.textContent = ''; }
    const mobileButton = donor.querySelector('.mobile-quick-add-btn');
    const oldMobileButton = next.querySelector('.mobile-quick-add-btn');
    if (oldMobileButton) {
      if (mobileButton) oldMobileButton.replaceWith(mobileButton.cloneNode(true)); else oldMobileButton.remove();
    } else if (mobileButton) {
      next.querySelector('.product-card-simple__title-row, .product-title-row')?.append(mobileButton.cloneNode(true));
    }
    if (mobileButton && !next.querySelector('quick-add-component')) {
      const quickAdd = source.querySelector('quick-add-component');
      if (quickAdd) next.append(quickAdd.cloneNode(true));
    }
    // Evita IDs duplicados al seleccionar un producto presente en otra tarjeta.
    const suffix = `-color-${++sequence}`;
    const ids = new Map();
    for (const node of next.querySelectorAll('[id]')) {
      if (node.getAttribute('ref') === 'productCardLink') continue;
      const old = node.id; node.id += suffix; ids.set(old, node.id);
    }
    for (const node of next.querySelectorAll('[for], [form], [aria-controls], [aria-labelledby], [aria-describedby]')) {
      for (const attr of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
        if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
      }
    }
    return next;
  }

  document.addEventListener('gco:color-select', event => {
    const picker = event.target.closest?.('gco-commons-color-picker[data-owner="theme"][data-context="card"]');
    const card = picker?.closest('product-card');
    const product = event.detail?.product;
    if (!card || !product || !card.querySelector('[ref="productCardLink"]')) return;
    event.preventDefault();
    const intent = {};
    pending.set(card, intent);
    if (String(product.productId) === card.dataset.productId) { card.removeAttribute('aria-busy'); return; }
    card.setAttribute('aria-busy', 'true');
    const focusWasInside = picker.contains(document.activeElement);
    load(product).then(template => {
      if (!card.isConnected || pending.get(card) !== intent) return;
      const next = prepare(card, template, product);
      const wrapper = card.parentElement?.matches('product-card-link') ? card.parentElement : null;
      if (wrapper) { wrapper.dataset.productId = String(product.productId); wrapper.dataset.featuredMediaUrl = next.dataset.featuredMediaUrl || ''; }
      card.replaceWith(next);
      next.dispatchEvent(new Event('pointerenter'));
      window.CustomPriceModule?.recalculateForCard?.(next);
      if (focusWasInside) {
        const host = next.querySelector('gco-commons-color-picker');
        Promise.resolve(host?.loadSiblings?.()).then(() => {
          if (next.isConnected) host?.querySelector('a[aria-current="page"]')?.focus({ preventScroll: true });
        });
      }
      next.dispatchEvent(new CustomEvent('gco:color-card-updated', { bubbles: true, detail: { productId: String(product.productId) } }));
    }).catch(error => {
      if (pending.get(card) === intent && card.isConnected) {
        card.removeAttribute('aria-busy');
        console.warn('[color-card] Selection unavailable; the next click can retry.', error);
      }
    });
  });
})();
